/**
 * Карта экранов и кодов диплинков. Раздел 7.2, 7.3.
 *
 * [G-A4] Коды коротки не для красоты: payload диплинка ограничен 64 символами
 * base64url (канон 11), поэтому внутрь кладётся короткий идентификатор
 * состояния, а не JSON.
 */

/** Четыре вкладки таббара. Раздел 7.3. */
export type Tab = 'T1' | 'T2' | 'T3' | 'T4' | 'T5'

export const TABS: Array<{ id: Tab; title: string; root: ScreenId }> = [
  { id: 'T1', title: 'Сводка', root: 'S-07' },
  { id: 'T2', title: 'Действия', root: 'S-20' },
  { id: 'T3', title: 'Товары', root: 'S-11' },
  { id: 'T4', title: 'Ещё', root: 'S-37' },
  // Просьба заказчика 15.10.2026: отдельное окно живого разговора с ИИ о магазине.
  { id: 'T5', title: 'Спросить', root: 'S-41' },
]

export type ScreenId =
  | 'S-00' | 'S-01' | 'S-02' | 'S-03' | 'S-04' | 'S-05' | 'S-06'
  | 'S-07' | 'S-08' | 'S-09' | 'S-10' | 'S-11' | 'S-12' | 'S-13' | 'S-14'
  | 'S-15' | 'S-16' | 'S-17' | 'S-18' | 'S-19' | 'S-20' | 'S-21' | 'S-22'
  | 'S-23' | 'S-24' | 'S-25' | 'S-26' | 'S-27' | 'S-28' | 'S-29' | 'S-30'
  | 'S-31' | 'S-32' | 'S-33' | 'S-34' | 'S-35' | 'S-36' | 'S-37' | 'S-38'
  | 'S-39' | 'S-40' | 'S-41' | 'S-42' | 'S-43' | 'S-44' | 'S-45'

export interface ScreenSpec {
  id: ScreenId
  title: string
  tab: Tab | null          // null — экран вне стека вкладок
  depth: number
  /** Код диплинка. null — экран недостижим ссылкой. */
  code: string | null
  /** Код содержит параметр: `s_<nmId>` и подобные. */
  param?: 'nm_id' | 'advert_id' | 'metric' | 'agent' | 'prompt' | 'entity' | 'period'
  /** Только для владельца. Раздел 11.3. */
  owner_only?: boolean
  /** Сотруднику — только с этим правом (решение 13.10.2026: права выдаёт владелец). */
  perm?: 'see.feedback' | 'see.stock' | 'see.ads' | 'see.money'
  /** Появится в v1.1, в MVP недоступен. Канон 15.9. */
  v1_1?: boolean
}

/**
 * Коды взяты из карты 7.2 дословно. Названия — видимые заголовки экранов (C:/tmp/rebrand/04-voice.md,
 * 3.1 и 3.14): «Утренние отчёты», «История решений», «Чего не хватает» — без слов «система», «журнал».
 * ⛔ Код экрана (S-13) и код ссылки (ads) наружу не выводятся: это внутренняя кухня.
 * S-35 и S-36 — служебные имена состояний; их видимые заголовки — в main.tsx.
 */
export const SCREENS: Record<ScreenId, ScreenSpec> = {
  'S-00': { id: 'S-00', title: 'Загрузка', tab: null, depth: 0, code: null },
  'S-01': { id: 'S-01', title: 'Как это работает', tab: 'T4', depth: 1, code: 'o' },
  'S-02': { id: 'S-02', title: 'Зачем нужен ключ', tab: null, depth: 0, code: null },
  'S-03': { id: 'S-03', title: 'Где взять ключ', tab: null, depth: 0, code: null },
  'S-04': { id: 'S-04', title: 'Ввод ключа', tab: null, depth: 0, code: null },
  'S-05': { id: 'S-05', title: 'Проверка ключа', tab: null, depth: 0, code: null },
  'S-06': { id: 'S-06', title: 'Ключ Wildberries', tab: 'T4', depth: 1, code: 'ks', owner_only: true },
  'S-07': { id: 'S-07', title: 'Сводка', tab: 'T1', depth: 0, code: 'd' },
  'S-08': { id: 'S-08', title: 'Разбор метрики', tab: 'T1', depth: 1, code: 'm', param: 'metric' },
  'S-09': { id: 'S-09', title: 'Утренние отчёты', tab: 'T1', depth: 1, code: 'rl' },
  'S-10': { id: 'S-10', title: 'Отчёт', tab: 'T1', depth: 2, code: 'r', param: 'agent' },
  'S-11': { id: 'S-11', title: 'Товары', tab: 'T3', depth: 0, code: 'sl' },
  'S-12': { id: 'S-12', title: 'Карточка товара', tab: 'T3', depth: 1, code: 's', param: 'nm_id' },
  'S-13': { id: 'S-13', title: 'Реклама', tab: 'T4', depth: 1, code: 'ads', perm: 'see.ads' },
  'S-14': { id: 'S-14', title: 'Кампания', tab: 'T4', depth: 2, code: 'c', param: 'advert_id', perm: 'see.ads' },
  'S-15': { id: 'S-15', title: 'Ставки и минус-фразы', tab: 'T4', depth: 3, code: null, perm: 'see.ads' },
  'S-16': { id: 'S-16', title: 'Остатки', tab: 'T4', depth: 1, code: 'st', perm: 'see.stock' },
  'S-17': { id: 'S-17', title: 'План поставок', tab: 'T4', depth: 2, code: 'sp', perm: 'see.stock' },
  'S-18': { id: 'S-18', title: 'Отзывы и вопросы', tab: 'T4', depth: 1, code: 'fb', perm: 'see.feedback' },
  'S-19': { id: 'S-19', title: 'Отзыв', tab: 'T4', depth: 2, code: 'f', param: 'entity', perm: 'see.feedback' },
  'S-20': { id: 'S-20', title: 'Действия', tab: 'T2', depth: 0, code: 'al' },
  'S-21': { id: 'S-21', title: 'Действие', tab: 'T2', depth: 1, code: 'a', param: 'entity' },
  'S-22': { id: 'S-22', title: 'История решений', tab: 'T2', depth: 1, code: 'log' },
  'S-23': { id: 'S-23', title: 'Агенты', tab: 'T4', depth: 1, code: 'ag' },
  'S-24': { id: 'S-24', title: 'Настройка отчёта', tab: 'T4', depth: 2, code: 'agc', param: 'agent' },
  'S-25': { id: 'S-25', title: 'Расписание и уведомления', tab: 'T4', depth: 1, code: 'sch' },
  'S-26': { id: 'S-26', title: 'Калькулятор прибыли', tab: 'T4', depth: 1, code: 'cst', owner_only: true },
  'S-27': { id: 'S-27', title: 'Калькулятор прибыли', tab: 'T4', depth: 2, code: 'cs', param: 'nm_id', owner_only: true },
  'S-28': { id: 'S-28', title: 'Расходы и налог', tab: 'T4', depth: 1, code: 'fx', owner_only: true },
  'S-29': { id: 'S-29', title: 'Обслуживание', tab: 'T4', depth: 1, code: 'ops', owner_only: true },
  'S-30': { id: 'S-30', title: 'Библиотека промтов', tab: 'T4', depth: 1, code: 'pl' },
  'S-31': { id: 'S-31', title: 'Промт', tab: 'T4', depth: 2, code: 'p', param: 'prompt' },
  'S-32': { id: 'S-32', title: 'Помощь', tab: 'T4', depth: 1, code: 'h' },
  'S-33': { id: 'S-33', title: 'Доступ и сотрудники', tab: 'T4', depth: 1, code: 'acc', owner_only: true },
  'S-34': { id: 'S-34', title: 'Экспорт', tab: 'T4', depth: 2, code: 'ex', v1_1: true },
  'S-35': { id: 'S-35', title: 'Открыто вне Telegram', tab: null, depth: 0, code: null },
  'S-36': { id: 'S-36', title: 'Доступ ограничен', tab: null, depth: 0, code: null },
  'S-37': { id: 'S-37', title: 'Ещё', tab: 'T4', depth: 0, code: 'more' },
  'S-38': { id: 'S-38', title: 'Поиск', tab: null, depth: 0, code: null },
  'S-39': { id: 'S-39', title: 'Чего не хватает', tab: 'T4', depth: 1, code: 'nv' },
  'S-40': { id: 'S-40', title: 'Замер времени', tab: 'T4', depth: 1, code: 'tm', owner_only: true },
  'S-41': { id: 'S-41', title: 'Спросить ИИ', tab: 'T5', depth: 0, code: 'ai' },
  'S-42': { id: 'S-42', title: 'Прошлые разговоры', tab: 'T5', depth: 1, code: 'aih' },
  'S-43': { id: 'S-43', title: 'Разговор', tab: 'T5', depth: 2, code: 'aic', param: 'entity' },
  // Срез прибыли по калькулятору (заказчик 21.10.2026). Параметр — период «ГГГГ-ММ-ДД_ГГГГ-ММ-ДД».
  'S-44': { id: 'S-44', title: 'Прибыль за период', tab: 'T4', depth: 1, code: 'pf', param: 'period', perm: 'see.money' },
  // Архив месяца в Excel (заказчик 02.11.2026): WB прячет свои отчёты, храним у себя.
  'S-45': { id: 'S-45', title: 'Архив месяцев', tab: 'T4', depth: 1, code: 'arc', perm: 'see.money' },
}

/** Обратный указатель: код диплинка → экран. */
export const BY_CODE: Record<string, ScreenSpec> = (() => {
  const m: Record<string, ScreenSpec> = {}
  for (const s of Object.values(SCREENS)) {
    if (s.code !== null) m[s.code] = s
  }
  return m
})()

export function resolveScreen(code: string | null): ScreenSpec {
  if (code === null || code === '') return SCREENS['S-07']   // старт по умолчанию
  return BY_CODE[code] ?? SCREENS['S-07']
}

/** Канон 11: payload диплинка не более 64 символов base64url. */
export const DEEPLINK_MAX_PAYLOAD = 64

export function buildStartApp(code: string, id?: string | number): string {
  const p = new URLSearchParams({ screen: code })
  if (id !== undefined) p.set('id', String(id))
  const payload = p.toString()
  if (payload.length > DEEPLINK_MAX_PAYLOAD) {
    throw new Error(
      `payload диплинка ${payload.length} символов при пределе ${DEEPLINK_MAX_PAYLOAD}. ` +
      'Внутрь кладётся короткий идентификатор состояния, а не JSON. Канон, раздел 11.')
  }
  return payload
}

/**
 * Права сотрудника из сессии. Ставятся один раз при входе (main.tsx); до этого — права нового
 * сотрудника по умолчанию (отзывы, остатки, реклама), как на сервере (access/permissions.ts).
 */
let viewerPerms: readonly string[] = ['see.feedback', 'see.stock', 'see.ads']
export function setViewerPerms(perms: readonly string[]): void { viewerPerms = perms }
/** Открыт ли раздел человеку: владельцу — всегда, сотруднику — по правам из сессии. */
export function viewerHas(role: 'owner' | 'staff', perm: string): boolean { return role === 'owner' || viewerPerms.includes(perm) }

/** [11.3] Сотруднику не показываем настройки владельца и разделы, которые ему не открыли. */
export function isVisibleFor(s: ScreenSpec, role: 'owner' | 'staff'): boolean {
  if (s.v1_1) return false
  if (role === 'owner') return true
  return !s.owner_only && (s.perm === undefined || viewerPerms.includes(s.perm))
}
